import type { Logger } from '@zg-rentals/logger-base';
import { Heading, Table } from '@zillow/constellation';
import { getBrowserRoutes as _getBrowserRoutes, getNodeRoutes as _getNodeRoutes } from '.';
import { routeConfig } from '.';
import { useAsync } from 'react-use';

const headingStyle = {
  background: '#fff',
  borderBottom: '1px solid #D1D1D5',
  padding: '20px',
};

export function RouteList({
  getBrowserRoutes = _getBrowserRoutes,
  getNodeRoutes = _getNodeRoutes,
  nodeSocketApi,
  logger,
  className = 'route-list',
}: {
  getBrowserRoutes?: () => Promise<typeof routeConfig.browserRoutes>;
  getNodeRoutes?: (socketApi?: string) => Promise<typeof routeConfig.nodeRoutes>;
  nodeSocketApi?: string;
  logger?: Logger;
  className?: string;
}) {
  const browserRoutes = useAsync(() => getBrowserRoutes().catch(logger?.error), [getBrowserRoutes, logger]);

  const nodeRoutes = useAsync(
    () => getNodeRoutes(nodeSocketApi).catch(logger?.error),
    [getNodeRoutes, nodeSocketApi, logger],
  );

  // @ts-ignore
  const browserRouteEntries = Object.entries(browserRoutes.value ?? routeConfig.browserRoutes);
  // @ts-ignore
  const nodeRouteEntries = Object.entries(nodeRoutes.value ?? routeConfig.nodeRoutes);

  return (
    <div className={className}>
      {browserRouteEntries.length > 0 && (
        <>
          <Heading level="5" style={headingStyle}>
            Browser routes:
          </Heading>
          <Table data-testid="browser-routes" appearance="zebra" aria-label="Browser routes">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Link</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body data-testid="browser-routes-body">
              {browserRouteEntries.map(([routeName, href]) => (
                <Table.Row key={routeName}>
                  <Table.Cell>{routeName}</Table.Cell>
                  <Table.Cell>
                    <a href={href}>{href}</a>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
      {nodeRouteEntries.length > 0 && (
        <>
          <Heading level="5" style={headingStyle}>
            Node routes:
          </Heading>
          <Table data-testid="node-routes" appearance="zebra" aria-label="Node routes">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Path pattern</Table.HeaderCell>
                <Table.HeaderCell>Middleware</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body data-testid="node-routes-body">
              {nodeRouteEntries.map(([pathPattern, middlewares]) => (
                <Table.Row key={pathPattern}>
                  <Table.Cell>{pathPattern}</Table.Cell>
                  <Table.Cell>{Object.keys(middlewares).join(', ')}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}
    </div>
  );
}
